import type { EntityId } from '../sim/entity';
import type { World } from '../sim/world';
import { canIgnoreName } from '../ignoreList';
import { isStormBody, isMonsterBody } from '../sim/content';

/**
 * The `/ignore` target-cursor gate (ADR-0185): unlike the duel/dice match filter this
 * is **not** range-limited and does not exclude NPCs - you can squelch any named,
 * ignorable, non-self sprite in view. `canIgnoreName` carries the self/blank/system-
 * herald veto, so a nameless sprite or your own is simply not a candidate (no hover, no
 * pick). Rats and event bodies still carry no ignore wiring on their sprites (the
 * renderer's scenery gate consults {@link isIgnorableScenery}), so they are never
 * offered either way.
 */
export function isIgnorableTarget(
  world: World | undefined,
  playerId: EntityId | undefined,
  viewerName: string,
  id: EntityId,
): boolean {
  if (id === playerId) return false;
  const name = world?.get(id)?.renderable?.name;
  return name !== undefined && canIgnoreName(name, viewerName);
}

/**
 * Which SCENERY-role mobiles the `/ignore` cursor may still pick (ADR-0266): with
 * roster NPCs classified as scenery (no duel wiring), the renderer's scenery
 * handlers are gated on this - kept to exactly the pre-D5 pickable set, i.e.
 * roster NPCs (players ride the duel-target wiring). Rats, event bodies
 * (storm/monster), and the marker NPCs (justice/escort/arena-master) carried no
 * target wiring before the role flip, so they stay unpickable here too. The
 * body/marker checks mirror the long-press menu's gates (now the installers'
 * contribution gates, Slice G).
 */
export function isIgnorableScenery(world: World | undefined, id: EntityId): boolean {
  const e = world?.get(id);
  if (!e || e.rat === true || e.justice !== undefined || e.escort !== undefined || e.arenaMaster === true) {
    return false;
  }
  return !isStormBody(e.renderable?.body) && !isMonsterBody(e.renderable?.body);
}
